import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { AppointmentReport, AppointmentReportAction } from '../../api/types';
import { ErrorMessage } from '../../components/ErrorMessage';
import { PageLoader } from '../../components/Loader';
import { Modal } from '../../components/Modal';
import { MutationError } from '../../components/MutationError';
import { MandateSection } from '../../pricing/MandateSection';
import { CancelNextChoice } from '../CancelNextChoice';
import { useProject } from '../../projects/context';
import { projectKeys, useProjectApi } from '../../projects/useProjectApi';
import { addDays, fmtDate, fromLocal, localDate } from '../format';
import {
  type Answers,
  type StepKey,
  agreementOptions,
  difficultyOptions,
  heldOptions,
  interestOptions,
  missingOptions,
  moodOptions,
  nextOptions,
  noInterestOptions,
  notPossibleNextOptions,
  notPossibleOptions,
  noteTargets,
  noteValue,
  onlyPrice,
  prune,
  reportSteps,
  stepComplete,
  stepTitles,
  toggle,
  withNote,
} from './reportSteps';

// Fragenkatalog nach dem Termin (OFFENE_FRAGEN B-29 Phase 2): Schritt für Schritt, jede Frage baut auf der vorigen Antwort auf;
// in jedem Schritt ist eine Notiz möglich, die KI wertet alle Notizen aus. Jeder Schritt wird als Entwurf gespeichert; vor dem
// Abschicken zeigt die Zusammenfassung, was danach automatisch passiert.
export function ReportWizard({
  appointmentId,
  title,
  goal,
  onClose,
}: {
  appointmentId: string;
  title: string;
  goal?: 'ON_SITE' | 'REMOTE';
  onClose: () => void;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const report = useQuery({
    queryKey: projectKeys.appointmentReport(project.id, appointmentId),
    queryFn: ({ signal }) => papi.appointmentReport(appointmentId, signal),
  });
  return (
    <Modal title={`Termin nachbereiten – ${title}`} onClose={onClose} size="wide">
      {report.isPending ? (
        <PageLoader label="Wird geladen …" />
      ) : report.isError ? (
        <ErrorMessage error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <WizardBody appointmentId={appointmentId} report={report.data} goal={goal} onClose={onClose} />
      )}
    </Modal>
  );
}

const roleLabels: Record<AppointmentReportAction['role'], string> = { SELF: 'Sie', TERMINIERER: 'Terminierer', SUPPORT: 'Support', SYSTEM: 'System' };

function initial(r: AppointmentReport): Answers {
  const a = r.answers ?? {};
  const facts = a.facts && Object.keys(a.facts).length > 0 ? a.facts : r.prefill;
  return { ...a, facts: facts ?? {} };
}

function WizardBody({
  appointmentId,
  report,
  goal,
  onClose,
}: {
  appointmentId: string;
  report: AppointmentReport;
  goal?: 'ON_SITE' | 'REMOTE';
  onClose: () => void;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<Answers>(() => initial(report));
  const [index, setIndex] = useState(0);
  const [planned, setPlanned] = useState<AppointmentReportAction[]>(report.planned);
  const steps = reportSteps(answers);
  const position = Math.min(index, steps.length - 1);
  const step: StepKey = steps[position] ?? 'held';
  const last = step === 'summary';
  const set = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }));
  const save = useMutation({
    mutationFn: (a: Answers) => papi.saveAppointmentReport(appointmentId, prune(a)),
    onSuccess: (r) => {
      setPlanned(r.planned);
      setIndex((i) => i + 1);
    },
  });
  const submit = useMutation({
    mutationFn: (a: Answers) => papi.submitAppointmentReport(appointmentId, prune(a)),
    onSuccess: (r) => {
      setPlanned(r.planned.length ? r.planned : planned);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });

  if (report.status === 'SUBMITTED' || submit.isSuccess) {
    return (
      <div className="report-done">
        <p className="alert alert-success" role="status">
          Danke – der Bericht ist gespeichert. Das passiert jetzt automatisch:
        </p>
        <ActionList actions={planned} />
        <p className="muted small">Die KI wertet Ihre Notizen gleich aus – geänderte Kontaktdaten und weitere Aufgaben landen beim Support bzw. bei Ihnen.</p>
        <div className="report-nav">
          <span />
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    );
  }
  if (!report.started) {
    return <p className="muted">Den Termin können Sie ab Terminbeginn nachbereiten.</p>;
  }
  const heading = step === 'facts' && onlyPrice(answers) ? 'Welche Preisvorstellung hat der Kunde?' : stepTitles[step];
  const known = steps.length > 1;
  const nextChoice = (
    <div className="report-block">
      <p className="report-sub">Wie geht es weiter?</p>
      <Choices
        options={step === 'notPossible' ? notPossibleNextOptions : nextOptions}
        value={answers.next_by}
        onPick={(v) => set({ next_by: v })}
        label="Wie geht es weiter?"
      />
      {answers.next_by === 'SELF' && <DateField label="Wann melden Sie sich?" value={answers.self_date} onChange={(v) => set({ self_date: v })} />}
      {answers.next_by === 'PHONE_SET' && (
        <>
          <div className="report-row">
            <DateTimeFields label="Telefontermin am" value={answers.phone_start} onChange={(v) => set({ phone_start: v })} />
            <label className="field grow">
              <span>Gesprächspartner (optional)</span>
              <input value={answers.phone_with ?? ''} maxLength={200} placeholder="z. B. Herr Gutmann (Sohn)" onChange={(e) => set({ phone_with: e.target.value })} />
            </label>
            <label className="field grow phone">
              <span>Telefonnummer (optional)</span>
              <input value={answers.phone_number ?? ''} maxLength={60} inputMode="tel" onChange={(e) => set({ phone_number: e.target.value })} />
            </label>
          </div>
          <p className="report-hint">Der Telefontermin landet in Ihrem Kalender und in Outlook. Der Kunde bekommt keine Bestätigungs-E-Mail.</p>
        </>
      )}
      {(answers.next_by === 'PHONE' || answers.next_by === 'PHONE_SET') && goal !== 'REMOTE' && (
        <p className="report-hint">Ein Telefontermin ist nur die zweitbeste Stufe (80 %) – das Ziel bleibt der Termin vor Ort.</p>
      )}
    </div>
  );

  return (
    <form
      className="report-wizard"
      onSubmit={(e) => {
        e.preventDefault();
        if (last) submit.mutate(answers);
        else save.mutate(answers);
      }}
    >
      <div className="report-progress" aria-hidden={!known}>
        <span className="muted small">{known ? `Schritt ${position + 1} von ${steps.length}` : 'Schritt 1'}</span>
        <span className="report-progress-bar">
          <span style={{ width: `${known ? ((position + 1) / steps.length) * 100 : 8}%` }} />
        </span>
      </div>
      <h3 className="report-question">{heading}</h3>
      {step === 'held' && (
        <>
          <Choices options={heldOptions} value={answers.held} onPick={(v) => set({ held: v })} label={stepTitles.held} />
          {answers.held === 'RESCHEDULED' && <NewStart value={answers.new_start} onChange={(v) => set({ new_start: v })} />}
          {answers.held === 'CANCELED' && (
            <div className="report-block">
              <label className="field wide">
                <span>Grund der Absage</span>
                <textarea rows={2} maxLength={2000} value={answers.cancel_reason ?? ''} onChange={(e) => set({ cancel_reason: e.target.value })} />
              </label>
              <CancelNextChoice value={answers.cancel_next} laterDate={answers.later_date} onChange={(n, later) => set({ cancel_next: n, later_date: later })} />
            </div>
          )}
          {answers.held === 'BROKER_MISSED' && (
            <p className="report-hint">Der Termin wird abgesagt. Der Terminierer ruft den Kunden heute an, entschuldigt sich und vereinbart einen neuen Termin.</p>
          )}
        </>
      )}
      {step === 'notPossible' && (
        <>
          <Choices
            options={notPossibleOptions}
            value={answers.not_possible_reason}
            label={stepTitles.notPossible}
            onPick={(v) => set({ not_possible_reason: v, recontact_6m: v === 'NO_TALK' ? true : answers.recontact_6m })}
          />
          <StepNote step="notPossible" answers={answers} set={set} />
          {answers.not_possible_reason === 'NO_TALK' && (
            <label className="checkbox report-block">
              <input type="checkbox" checked={!!answers.recontact_6m} onChange={(e) => set({ recontact_6m: e.target.checked })} /> In 6 Monaten erneut anfragen
            </label>
          )}
          {answers.not_possible_reason && answers.not_possible_reason !== 'NO_TALK' && nextChoice}
        </>
      )}
      {step === 'noShow' && nextChoice}
      {step === 'mood' && (
        <>
          <Choices options={moodOptions} value={answers.mood} onPick={(v) => set({ mood: v })} label={stepTitles.mood} />
          {answers.mood === 'DIFFICULT' && (
            <div className="report-block">
              <p className="report-sub">Was war schwierig?</p>
              <Chips options={difficultyOptions} values={answers.difficulties} onToggle={(v) => set({ difficulties: toggle(answers.difficulties, v) })} label="Was war schwierig?" />
            </div>
          )}
        </>
      )}
      {step === 'interest' && (
        <>
          <Choices
            options={interestOptions}
            value={answers.interest}
            label={stepTitles.interest}
            onPick={(v) =>
              set({
                interest: v,
                follow_up_by: v === 'LIKELY' ? 'SELF' : v === 'UNDECIDED' ? 'TERMINIERER' : answers.follow_up_by,
                follow_up_at: answers.follow_up_at || addDays(localDate(), 7),
                recontact_6m: v === 'NONE' ? true : answers.recontact_6m,
              })
            }
          />
          {answers.interest === 'MANDATE' && (
            <div className="report-block report-mandate">
              <p className="report-hint">Bitte den Auftrag hier melden – Art, Datum und ggf. Laufzeit.</p>
              <MandateSection appointmentId={appointmentId} />
            </div>
          )}
          {(answers.interest === 'LIKELY' || answers.interest === 'UNDECIDED') && (
            <div className="report-block">
              <p className="report-sub">Woran hängt es noch?</p>
              <Chips options={missingOptions} values={answers.missing} onToggle={(v) => set({ missing: toggle(answers.missing, v) })} label="Woran hängt es noch?" />
              <div className="report-row">
                <DateField label="Wann nachfassen?" value={answers.follow_up_at} onChange={(v) => set({ follow_up_at: v })} />
                <div className="field">
                  <span>Wer fasst nach?</span>
                  <Segmented
                    options={[
                      { value: 'SELF', label: 'Ich selbst' },
                      { value: 'TERMINIERER', label: 'Terminierer' },
                    ]}
                    value={answers.follow_up_by}
                    onPick={(v) => set({ follow_up_by: v })}
                    label="Wer fasst nach?"
                  />
                </div>
              </div>
            </div>
          )}
          {answers.interest === 'NONE' && (
            <div className="report-block">
              <p className="report-sub">Warum nicht?</p>
              <Chips
                options={noInterestOptions}
                values={answers.no_interest_reason ? [answers.no_interest_reason] : []}
                onToggle={(v) => set({ no_interest_reason: v })}
                label="Warum nicht?"
              />
              <label className="checkbox">
                <input type="checkbox" checked={!!answers.recontact_6m} onChange={(e) => set({ recontact_6m: e.target.checked })} /> In 6 Monaten erneut anfragen
              </label>
            </div>
          )}
        </>
      )}
      {step === 'facts' && <FactsStep answers={answers} set={set} />}
      {step === 'agreements' && (
        <ul className="report-agreements" role="group" aria-label={stepTitles.agreements}>
          {agreementOptions(answers.interest).map((o) => {
            const on = !!answers.agreements?.includes(o.value);
            return (
              <li key={o.value} className={on ? 'active' : undefined}>
                <label className="checkbox">
                  <input type="checkbox" checked={on} onChange={() => set({ agreements: toggle(answers.agreements, o.value) })} />
                  <span>
                    {o.label}
                    <small>{o.hint}</small>
                  </span>
                </label>
                {o.value === 'SECOND_APPOINTMENT' && on && (
                  <label className="field">
                    <span>Wunschtermin bzw. Hinweis</span>
                    <input value={answers.second_wish ?? ''} maxLength={2000} onChange={(e) => set({ second_wish: e.target.value })} />
                  </label>
                )}
                {o.value === 'CALLBACK' && on && (
                  <DateField label="Rückruf am" value={answers.callback_at ?? addDays(localDate(), 1)} onChange={(v) => set({ callback_at: v })} />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {step === 'summary' && (
        <>
          <p className="report-hint">Das passiert nach dem Abschicken automatisch:</p>
          <ActionList actions={planned} />
          <p className="muted small">Die KI wertet alle Notizen aus: Geänderte Kontaktdaten prüft und übernimmt der Support, weitere Aufgaben werden angelegt.</p>
        </>
      )}
      {(step !== 'held' || answers.held) && step !== 'notPossible' && <StepNote step={step} answers={answers} set={set} />}
      {(save.isError || submit.isError) && <MutationError error={save.error ?? submit.error} />}
      <div className="report-nav">
        {index > 0 ? (
          <button type="button" className="btn btn-ghost" onClick={() => setIndex((i) => Math.max(0, i - 1))}>
            Zurück
          </button>
        ) : (
          <span />
        )}
        <button type="submit" className="btn btn-primary" disabled={!stepComplete(step, answers) || save.isPending || submit.isPending}>
          {last ? 'Abschicken' : 'Weiter'}
        </button>
      </div>
    </form>
  );
}

// StepNote: die Notiz des Schritts – wächst mit dem Text (höchstens rund acht Zeilen, dann blättern).
function StepNote({ step, answers, set }: { step: StepKey; answers: Answers; set: (p: Partial<Answers>) => void }) {
  const t = noteTargets[step];
  const required = step === 'notPossible' && answers.not_possible_reason === 'OTHER';
  return (
    <label className="field report-note">
      <span>{required ? 'Was ist passiert? (bitte kurz beschreiben)' : t.label}</span>
      <textarea rows={2} maxLength={2000} value={noteValue(step, answers)} placeholder={t.placeholder} onChange={(e) => set(withNote(step, answers, e.target.value))} />
    </label>
  );
}

function Choices<T extends string>({
  options,
  value,
  onPick,
  label,
}: {
  options: readonly { value: T; label: string; hint?: string }[];
  value: string | undefined;
  onPick: (v: T) => void;
  label: string;
}) {
  return (
    <div className="choice-grid" role="group" aria-label={label} data-count={options.length}>
      {options.map((o) => (
        <button key={o.value} type="button" className={value === o.value ? 'choice active' : 'choice'} aria-pressed={value === o.value} onClick={() => onPick(o.value)}>
          <span>{o.label}</span>
          {o.hint && <small>{o.hint}</small>}
        </button>
      ))}
    </div>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onPick,
  label,
}: {
  options: readonly { value: T; label: string }[];
  value: string | undefined;
  onPick: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className={value === o.value ? 'active' : undefined} aria-pressed={value === o.value} onClick={() => onPick(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Chips<T extends string>({
  options,
  values,
  onToggle,
  label,
}: {
  options: readonly { value: T; label: string }[];
  values: string[] | undefined;
  onToggle: (v: T) => void;
  label: string;
}) {
  return (
    <div className="chips" role="group" aria-label={label}>
      {options.map((o) => {
        const on = !!values?.includes(o.value);
        return (
          <button key={o.value} type="button" className={on ? 'chip active' : 'chip'} aria-pressed={on} onClick={() => onToggle(o.value)}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function DateField({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: string) => void }) {
  return (
    <label className="field narrow">
      <span>{label}</span>
      <input type="date" value={value ?? ''} min={localDate()} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

// DateTimeFields: Datum und Uhrzeit nebeneinander (z. B. vor Ort abgesprochener Telefontermin).
function DateTimeFields({ label, onChange }: { label: string; value: string | undefined; onChange: (iso: string | undefined) => void }) {
  const [parts, setParts] = useState({ date: '', time: '18:00' });
  const update = (p: { date: string; time: string }) => {
    setParts(p);
    onChange(p.date && p.time ? fromLocal(p.date, p.time) : undefined);
  };
  return (
    <>
      <label className="field narrow">
        <span>{label}</span>
        <input type="date" value={parts.date} min={localDate()} onChange={(e) => update({ ...parts, date: e.target.value })} />
      </label>
      <label className="field narrow">
        <span>um</span>
        <input type="time" value={parts.time} onChange={(e) => update({ ...parts, time: e.target.value })} />
      </label>
    </>
  );
}

function NewStart({ value, onChange }: { value: string | undefined; onChange: (iso: string | undefined) => void }) {
  const [parts, setParts] = useState({ date: '', time: '10:00' });
  const update = (p: { date: string; time: string }) => {
    setParts(p);
    onChange(p.date && p.time ? fromLocal(p.date, p.time) : undefined);
  };
  return (
    <div className="report-block">
      <div className="report-row">
        <label className="field narrow">
          <span>Neuer Termin am</span>
          <input type="date" value={parts.date} min={localDate()} onChange={(e) => update({ ...parts, date: e.target.value })} />
        </label>
        <label className="field narrow">
          <span>um</span>
          <input type="time" value={parts.time} onChange={(e) => update({ ...parts, time: e.target.value })} />
        </label>
      </div>
      {value && <p className="report-hint">Der Kunde bekommt die neue Terminbestätigung, die Terminplanung bestätigt telefonisch.</p>}
    </div>
  );
}

function FactsStep({ answers, set }: { answers: Answers; set: (p: Partial<Answers>) => void }) {
  const f = answers.facts ?? {};
  const num = (key: 'price' | 'living_area' | 'plot_area' | 'year_built') => (e: { target: { value: string } }) => {
    const v = e.target.value.replace(/\D/g, '');
    set({ facts: { ...f, [key]: v ? Number(v) : undefined } });
  };
  if (onlyPrice(answers)) {
    return (
      <>
        <p className="report-hint">Hilft beim erneuten Anfragen in sechs Monaten.</p>
        <label className="field fact">
          <span>Preisvorstellung (€)</span>
          <input inputMode="numeric" value={f.price ?? ''} onChange={num('price')} />
        </label>
      </>
    );
  }
  return (
    <>
      <p className="report-hint">Vorbelegt mit dem, was schon bekannt ist – bitte bestätigen oder korrigieren.</p>
      <div className="report-facts">
        <label className="field fact">
          <span>Preisvorstellung (€)</span>
          <input inputMode="numeric" value={f.price ?? ''} onChange={num('price')} />
        </label>
        <label className="field fact">
          <span>Wohnfläche (m²)</span>
          <input inputMode="numeric" value={f.living_area ?? ''} onChange={num('living_area')} />
        </label>
        <label className="field fact">
          <span>Grundstück (m²)</span>
          <input inputMode="numeric" value={f.plot_area ?? ''} onChange={num('plot_area')} />
        </label>
        <label className="field fact">
          <span>Baujahr</span>
          <input inputMode="numeric" maxLength={4} value={f.year_built ?? ''} onChange={num('year_built')} />
        </label>
      </div>
    </>
  );
}

function ActionList({ actions }: { actions: AppointmentReportAction[] }) {
  if (actions.length === 0) return <p className="muted">Noch nichts geplant.</p>;
  return (
    <ul className="report-actions">
      {actions.map((a) => (
        <li key={`${a.kind}-${a.label}`}>
          <span className="badge badge-muted">{roleLabels[a.role]}</span>
          <span>
            {a.label}
            {a.due_at && a.kind !== 'TASK' && a.kind !== 'FOLLOW_UP' ? <span className="muted"> · bis {fmtDate(a.due_at)}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
